import Link from 'next/link';
import React, { useContext, useCallback, useRef } from 'react';
import cx from 'classnames';
import { OpenAPIObject } from 'openapi3-ts';

import Context from '../../../contexts/docs';
import OpenAPIContext from '../../../contexts/docs/openapi';
import { isApiRoute } from '../utilities';

import styles from './index.module.scss';
import HideFromUser from 'src/components-v2/Helpers/HideFromUser/HideFromUser';

interface Props {
  Component: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  id: string;
  children: React.ReactElement;
}

// Helper function to find anchor element from click target
const findAnchorElement = (target: HTMLElement): HTMLAnchorElement | null => {
  let current = target;
  while (current && current !== document.body) {
    if (current.tagName === 'A') {
      return current as HTMLAnchorElement;
    }
    current = current.parentElement!;
  }
  return null;
};

// Helper function to calculate scroll position for sticky elements
const calculateScrollPosition = (
  targetElement: HTMLElement,
  scrollContainer: Element,
): number => {
  let referenceElement = targetElement.parentElement;
  let cumulativeOffset = 0;

  // Walk up to find an element that's not sticky
  while (referenceElement && referenceElement !== scrollContainer) {
    const computedStyle = window.getComputedStyle(referenceElement);
    if (computedStyle.position !== 'sticky') {
      cumulativeOffset = referenceElement.offsetTop;
      break;
    }
    referenceElement = referenceElement.parentElement;
  }

  // Probably unnecessary fallback: calculate offset relative to scroll container
  if (cumulativeOffset === 0) {
    let element = targetElement;
    while (element && element !== scrollContainer) {
      if (element.offsetParent === scrollContainer) {
        cumulativeOffset = element.offsetTop;
        break;
      }
      element = element.parentElement!;
    }
  }

  return cumulativeOffset;
};

const LinkedHeading: React.FC<Props> = ({ Component, id, children }) => {
  const { tableOfContents, metadata } = useContext(Context);
  const definition: OpenAPIObject | null = useContext(OpenAPIContext);
  const handlerRef = useRef<((e: Event) => void) | null>(null);

  // Check if this should have sticky styling (H4 on reference pages)
  const shouldBeSticky = Component === 'h4' && metadata?.layout === 'reference';

  const handleAnchorNavigation = useCallback((e: Event) => {
    const anchorElement = findAnchorElement(e.target as HTMLElement);
    if (!anchorElement?.hash) return;

    const anchorId = anchorElement.hash.substring(1);
    const targetElement = document.getElementById(anchorId);
    const scrollContainer = document.querySelector('.docs-scroll-container');

    if (!targetElement || !scrollContainer) return;

    // Only handle H4 anchors that would be sticky
    const headingElement = targetElement.closest('h4');
    if (!headingElement) return;

    e.preventDefault();

    const scrollToPosition = calculateScrollPosition(
      targetElement,
      scrollContainer,
    );

    setTimeout(() => {
      scrollContainer.scrollTo({
        top: scrollToPosition,
        behavior: 'smooth',
      });
    }, 0);
  }, []);

  // Add global anchor navigation interceptor for reference pages
  React.useEffect(() => {
    if (!shouldBeSticky) return;

    // Avoid duplicate listeners
    if (handlerRef.current) return;

    handlerRef.current = handleAnchorNavigation;
    document.addEventListener('click', handleAnchorNavigation);

    return () => {
      if (handlerRef.current) {
        document.removeEventListener('click', handlerRef.current);
        handlerRef.current = null;
      }
    };
  }, [shouldBeSticky, handleAnchorNavigation]);

  // return SSR version of component so that headers are included in initial payload
  if (tableOfContents == null) {
    return (
      <>
        {shouldBeSticky && <HideFromUser>=*=*=*=</HideFromUser>}
        <Link href={`#${id}`} passHref>
          <a href={`#${id}`}>
            <Component
              className={cx(
                styles.heading,
                shouldBeSticky && styles.stickyHeading,
              )}
            >
              {children}
              <span
                id={id}
                className={styles.offsetAnchor}
                data-algolia='linkedHeadingAnchor'
              />
            </Component>
          </a>
        </Link>
      </>
    );
  }

  const childrenIsApiRoute = isApiRoute(children, definition);
  if (id == null) {
    return <Component>{children}</Component>;
  }
  return (
    <>
      {shouldBeSticky && <HideFromUser>=*=*=*=</HideFromUser>}

      <Link href={`#${id}`}>
        <a>
          <Component
            className={cx(
              styles.heading,
              shouldBeSticky && styles.stickyHeading,
              childrenIsApiRoute && 'noMargin',
            )}
          >
            {childrenIsApiRoute ? <code>{children}</code> : children}
            <span
              id={id}
              className={styles.offsetAnchor}
              data-algolia='linkedHeadingAnchor'
            />
          </Component>
        </a>
      </Link>
    </>
  );
};

LinkedHeading.displayName = 'LinkedHeading';

export default LinkedHeading;
